import apiService from '../utils/apiService';

/**
 * SMS feedback (опитувальник для SMS каналу).
 *
 * Усі запити цього флоу зібрані тут:
 *   POST api/cda/users/phone/auth   — авторизація по номеру, повертає jwt
 *   GET  sms-feedback/availability  — чи можна залишити відгук цього місяця (jwt)
 *   GET  api/cda/brands/extended    — список брендів/форматів для двох селектів
 *   GET  sku-name                   — продукти кампанії для Q2 (jwt)
 *   POST sms-feedback               — збереження відгуку (jwt)
 */

const TOKEN_KEY = 's_f_t';

/** Коди помилок бекенду, на які флоу реагує окремими екранами. */
export const SMS_FEEDBACK_ERROR_CODES = {
  USER_NOT_FOUND: 100002,
  MONTHLY_LIMIT_EXCEEDED: 100011
};

/**
 * Бекенд віддає код помилки в тілі відповіді. Форма тіла може відрізнятись
 * між ендпоінтами, тому перевіряємо всі відомі варіанти й приводимо до числа.
 */
export const getErrorCode = (error) => {
  const data = error?.response?.data;
  if (!data) return null;

  const rawCode =
    data.code ??
    data.errorCode ??
    data.error?.code ??
    data.data?.code ??
    (typeof data.error === 'number' ? data.error : null);

  const code = Number(rawCode);

  return Number.isFinite(code) ? code : null;
};

export const isErrorCode = (error, code) => getErrorCode(error) === code;

const setToken = (token) => {
  if (!token) return;

  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch (e) {}
};

export const getToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

export const clearToken = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {}
};

const getAuthConfig = () => {
  const token = getToken();

  return token
    ? {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    : {};
};

/**
 * Авторизація споживача за номером телефону.
 * Успіх — у відповіді приходить токен, який далі використовується для
 * `getAvailability` та `createSmsFeedback`.
 * Якщо споживача немає в базі — бекенд віддає UserNotFound (100002).
 */
const authByPhone = async (phone) => {
  const response = await apiService.post('api/cda/users/phone/auth', {
    data: { phone }
  });

  const data = response?.data;
  const token =
    data?.token ?? data?.accessToken ?? data?.access_token ?? data?.jwt;

  setToken(token);

  return data;
};

/**
 * Чи доступний відгук для цієї кампанії зараз.
 * Ендпоінт закритий jwt, тому викликається вже після `authByPhone`.
 */
const getAvailability = async (campaignName) => {
  const response = await apiService.get('sms-feedback/availability', {
    params: { campaignName },
    ...getAuthConfig()
  });

  return response?.data;
};

/**
 * Бекенд може віддати бренди як масив, так і загорнутими в об'єкт, а дочірні
 * формати — під різними ключами. Зводимо все до { id, label, children }, де
 * label — це `public_description`, який і треба відправляти назад у відгуку.
 */
const normalizeBrand = (brand) => {
  const children =
    brand?.children ?? brand?.formats ?? brand?.items ?? brand?.child ?? [];

  return {
    id: brand?.id ?? brand?.uuid ?? brand?.public_description,
    label:
      brand?.public_description ??
      brand?.publicDescription ??
      brand?.description ??
      brand?.name ??
      '',
    children: Array.isArray(children) ? children.map(normalizeBrand) : []
  };
};

const getBrands = async () => {
  const response = await apiService.get('api/cda/brands/extended');

  const data = response?.data;
  const list = Array.isArray(data)
    ? data
    : (data?.items ?? data?.data ?? data?.brands ?? []);

  return (Array.isArray(list) ? list : [])
    .map(normalizeBrand)
    .filter((brand) => brand.label);
};

/**
 * Бекенд може віддати список як масив рядків, так і масив об'єктів з різними
 * назвами поля, тому зводимо все до рядка — саме його і треба відправити назад
 * у відгуку полем `feedbackBrand`.
 */
const normalizeSkuName = (item) => {
  if (typeof item === 'string') return item.trim();

  return (
    item?.sku_name ??
    item?.skuName ??
    item?.name ??
    item?.label ??
    item?.public_description ??
    ''
  ).trim();
};

/**
 * `skuCode` для /sku-name — це не весь campaign_name, а код бренду з нього:
 * частина після префікса `UA-` (26UA-MLB -> MLB, 26UA-CHES -> CHES).
 * Регістр у посиланні може відрізнятися, тому пошук префікса нечутливий до
 * нього; якщо `UA-` у коді немає, відправляємо campaign_name як є.
 */
const getBrandCode = (campaignName) => {
  const match = /ua-(.+)$/i.exec(campaignName || '');

  return match ? match[1] : campaignName;
};

/**
 * Список продуктів для питання "Хочу залишити відгук на:" (26UA-CHE і
 * 26UA-MRLB). Набір залежить від бренду з посилання. Ендпоінт закритий jwt,
 * як і `availability`, тому викликається вже після `authByPhone`.
 */
const getSkuNames = async (campaignName) => {
  const response = await apiService.get('sku-name', {
    params: {
      isVisibleInSmsFeedback: true,
      skuCode: getBrandCode(campaignName)
    },
    ...getAuthConfig()
  });

  const data = response?.data;
  const list = Array.isArray(data)
    ? data
    : (data?.items ?? data?.data ?? data?.skuNames ?? []);

  const names = (Array.isArray(list) ? list : [])
    .map(normalizeSkuName)
    .filter(Boolean);

  // Один і той самий sku_name може прийти від кількох product_matches —
  // у списку він має бути один раз.
  return [...new Set(names)];
};

/**
 * Збереження відгуку. Обов'язкові поля бекенду: campaignName, productSeen,
 * gender — решта необов'язкові, тому порожні значення не відправляємо.
 */
const createSmsFeedback = async (payload) => {
  const data = Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    )
  );

  const response = await apiService.post('sms-feedback', {
    data,
    ...getAuthConfig()
  });

  return response?.data;
};

export default {
  authByPhone,
  getAvailability,
  getBrands,
  getSkuNames,
  createSmsFeedback,
  getToken,
  clearToken
};
